import React, { useMemo, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlaceIcon from '@mui/icons-material/Place';
import DownloadIcon from '@mui/icons-material/Download';
import logger from '../../../utils/logger';
import GMBService from '../../../services/GMBService';
import ApiService from '../../../services/api';
import GeotagService from '../../../services/GeotagService';
import { applyGeotagMetadata, inspectGeotagMetadata, normalizeGeotagMetadata } from '../../../utils/geotagging';

const KEYWORD_BLACKLIST = /^(img|image|screenshot|photo|file|template)\d*$/i;
const FILE_KEYWORD_STOPWORDS = /(visibeen|template|design|edm|\bimg\b|\bimage\b|\bscreenshot\b|\bphone\b|\bphoto\b)/i;

const readFilePreview = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => resolve(event.target?.result || '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result || '');
  reader.onerror = () => reject(new Error('Failed to read image data'));
  reader.readAsDataURL(blob);
});

const fetchImageAsDataUrl = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  return blobToDataUrl(blob);
};

const TOPIC_DESCRIPTIONS = {
  STANDARD: 'general business update',
  EVENT: 'event announcement',
  OFFER: 'limited-time offer',
  ALERT: 'important alert'
};

const formatAddressString = (address) => {
  if (!address) return '';
  if (typeof address === 'string') {
    return address.replace(/\s+/g, ' ').replace(/,\s*,/g, ', ').trim();
  }
  if (typeof address === 'object') {
    const parts = [];
    if (Array.isArray(address.addressLines)) {
      parts.push(address.addressLines.filter(Boolean).join(', '));
    }
    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);
    return parts.filter(Boolean).join(', ');
  }
  return '';
};

const normalizeKeywordInput = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(item => String(item || '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(/[,|]/)
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

const buildImageHint = (hasImage, topicDescriptor) => {
  if (!hasImage) {
    return `No specific image details were provided. Imagine a visual that matches the "${topicDescriptor}" theme and describe it to support the offer.`;
  }
  return `An image accompanies this post. Describe what customers likely see that aligns with the "${topicDescriptor}" update—focus on setting, subjects, and emotions without referencing internal filenames or template codes.`;
};

const buildKeywordHints = ({ sourceKeywords, businessCategory, topicType }) => {
  const keywords = normalizeKeywordInput(sourceKeywords);
  if (businessCategory) keywords.push(businessCategory);

  if (topicType && topicType !== 'STANDARD') {
    keywords.push(TOPIC_DESCRIPTIONS[topicType] || topicType);
  }

  const seen = new Set();
  const deduped = [];
  keywords.forEach((keyword) => {
    const normalized = keyword.toLowerCase();
    if (
      !seen.has(normalized) &&
      !KEYWORD_BLACKLIST.test(normalized) &&
      !FILE_KEYWORD_STOPWORDS.test(normalized)
    ) {
      seen.add(normalized);
      deduped.push(keyword);
    }
  });

  return deduped.slice(0, 6);
};

const sanitizeHashtagOutput = (raw) => {
  if (!raw) return [];
  return raw
    .split(/[\s,|]+/)
    .map(token => token.trim())
    .filter(Boolean)
    .map(token => (token.startsWith('#') ? token : `#${token}`))
    .map(token => `#${token.slice(1).replace(/[^a-z0-9]/gi, '')}`)
    .filter(token => token.length > 1)
    .map(token => `#${token.slice(1, 28)}`) // cap length for readability
    .filter(Boolean)
    .filter((token, index, arr) => arr.indexOf(token) === index)
    .slice(0, 4);
};

const sanitizeHashtagArray = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .map(item => (item.startsWith('#') ? item : `#${item}`))
    .map(item => `#${item.slice(1).replace(/[^a-z0-9]/gi, '')}`)
    .filter(item => item.length > 1)
    .map(item => `#${item.slice(1, 28)}`)
    .filter((item, index, arr) => item && arr.indexOf(item) === index)
    .slice(0, 8);
};

const sanitizeKeywordArray = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .map(item => item.toLowerCase())
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .slice(0, 5);
};

const extractJsonPayload = (raw = '') => {
  if (!raw) return null;
  const trimmed = raw.trim()
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .trim();
  const match = trimmed.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    return JSON.parse(match[0]);
  } catch (error) {
    logger.warn('[SchedulePostModal] Failed to parse vision JSON payload:', { raw: match[0] });
    return null;
  }
};

const convertFileToDataUrl = (file) => new Promise((resolve, reject) => {
  if (!(file instanceof File)) {
    reject(new Error('Invalid file provided'));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = (err) => reject(err);
  reader.readAsDataURL(file);
});

// Helper function to get best posting times based on business category
const getBestPostingTimes = (category) => {
  const categoryLower = (category || '').toLowerCase();

  if (categoryLower.includes('salon') || categoryLower.includes('spa') || categoryLower.includes('beauty')) {
    return {
      days: 'Tuesday–Saturday',
      times: '10:00–11:00 AM, 6:00–7:30 PM',
      frequency: 'Daily',
      reason: 'Users check self-care offers before/after work'
    };
  }

  if (categoryLower.includes('restaurant') || categoryLower.includes('cafe') || categoryLower.includes('food')) {
    return {
      days: 'Wednesday–Sunday',
      times: '11:30 AM–1:30 PM, 7:00–9:30 PM',
      frequency: '4–5x/week',
      reason: 'Lunch & dinner discovery windows'
    };
  }

  if (categoryLower.includes('doctor') || categoryLower.includes('clinic') || categoryLower.includes('medical')) {
    return {
      days: 'Monday–Friday',
      times: '9:00–11:00 AM, 5:00–7:00 PM',
      frequency: '2–3x/week',
      reason: 'People plan appointments early or after work'
    };
  }

  if (categoryLower.includes('gym') || categoryLower.includes('fitness') || categoryLower.includes('yoga')) {
    return {
      days: 'Monday–Saturday',
      times: '6:00–8:00 AM, 6:00–8:00 PM',
      frequency: '4–5x/week',
      reason: 'Health-conscious audience active morning/evening'
    };
  }

  if (categoryLower.includes('real estate') || categoryLower.includes('builder') || categoryLower.includes('property')) {
    return {
      days: 'Tuesday–Saturday',
      times: '10:00 AM–1:00 PM, 4:00–6:00 PM',
      frequency: '3x/week',
      reason: 'Decision-makers engage mid-day'
    };
  }

  if (categoryLower.includes('education') || categoryLower.includes('coaching') || categoryLower.includes('tuition')) {
    return {
      days: 'Tuesday–Sunday',
      times: '9:00–11:00 AM, 4:00–7:00 PM',
      frequency: '3x/week',
      reason: 'Parents & students browse after school hours'
    };
  }

  if (categoryLower.includes('automotive') || categoryLower.includes('car') || categoryLower.includes('service center')) {
    return {
      days: 'Monday–Saturday',
      times: '9:30–11:30 AM, 5:00–7:00 PM',
      frequency: '3x/week',
      reason: 'People plan car service before work or after office'
    };
  }

  if (categoryLower.includes('retail') || categoryLower.includes('electronics') || categoryLower.includes('fashion') || categoryLower.includes('store')) {
    return {
      days: 'Tuesday–Sunday',
      times: '11:00 AM–2:00 PM, 7:00–9:00 PM',
      frequency: '4x/week',
      reason: 'Shopping mindset peaks pre-lunch & evening'
    };
  }

  if (categoryLower.includes('hotel') || categoryLower.includes('resort') || categoryLower.includes('travel')) {
    return {
      days: 'Wednesday–Sunday',
      times: '12:00–2:00 PM, 8:00–9:30 PM',
      frequency: '3x/week',
      reason: 'Users plan travel around lunch/evening'
    };
  }

  if (categoryLower.includes('plumber') || categoryLower.includes('electrician') || categoryLower.includes('home service')) {
    return {
      days: 'Monday–Saturday',
      times: '9:00–11:00 AM, 5:00–6:00 PM',
      frequency: '3x/week',
      reason: 'Homeowners plan maintenance after breakfast/after work'
    };
  }

  if (categoryLower.includes('dentist') || categoryLower.includes('dental')) {
    return {
      days: 'Monday–Saturday',
      times: '10:00 AM–1:00 PM, 6:00–7:00 PM',
      frequency: '2–3x/week',
      reason: 'Consistent engagement during clinic hours'
    };
  }

  if (categoryLower.includes('university') || categoryLower.includes('institution')) {
    return {
      days: 'Tuesday–Friday',
      times: '10:00 AM–1:00 PM',
      frequency: '2x/week',
      reason: 'B2B-style informational audience'
    };
  }

  if (categoryLower.includes('event') || categoryLower.includes('photographer') || categoryLower.includes('videographer')) {
    return {
      days: 'Wednesday–Saturday',
      times: '11:00 AM–2:00 PM, 8:00–10:00 PM',
      frequency: '4x/week',
      reason: 'Browsing peaks around leisure hours'
    };
  }

  // Default for other categories
  return {
    days: 'Tuesday–Saturday',
    times: '10:00 AM–12:00 PM, 5:00–7:00 PM',
    frequency: '3x/week',
    reason: 'General business engagement patterns'
  };
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '600px',
    width: '100%',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      margin: '0',
      width: '100%',
      maxWidth: '100%',
      height: '100dvh',
      maxHeight: '100dvh',
      borderRadius: '0',
      padding: '0'
    }
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 600,
  color: '#0B91D6',
  padding: '24px 24px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    padding: '12px 12px 8px',
    flexShrink: 0,
    borderBottom: '1px solid #E0E0E0'
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  overflowY: 'auto !important',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  flex: '1 1 auto',
  [theme.breakpoints.down('sm')]: {
    padding: '16px 12px',
    gap: '16px',
    overflowY: 'auto !important',
    overflowX: 'hidden',
    flex: '1 1 auto',
    minHeight: 0,
    paddingBottom: '112px' // space for sticky footer
  }
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px 24px',
  gap: '12px',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
    flexDirection: 'column-reverse',
    flexShrink: 0,
    borderTop: '1px solid #E0E0E0',
    position: 'sticky',
    bottom: 0,
    backgroundColor: '#fff',
    zIndex: 2,
    boxShadow: '0 -6px 12px rgba(0,0,0,0.06)',
    '& > button': {
      width: '100%',
      margin: '0 !important'
    }
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  padding: '12px 32px',
  borderRadius: '8px',
  textTransform: 'none',
  '&.primary': {
    backgroundColor: '#0B91D6',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#0A7AB8'
    },
    '&:disabled': {
      backgroundColor: '#E0E0E0',
      color: '#999999'
    }
  },
  '&.secondary': {
    backgroundColor: '#F5F5F5',
    color: '#30302E',
    '&:hover': {
      backgroundColor: '#E0E0E0'
    }
  }
}));

const SchedulePostModal = ({
  open,
  onClose,
  accountId,
  locationId,
  onPostScheduled,
  onSuccess,  // Add onSuccess for PostGenerationResults compatibility
  taskCreationMode = false,  // NEW: explicitly control admin task mode
  businessCategory,
  businessName = '',  // Add business name for AI context
  businessAddress = '',
  businessKeywords = [],
  businessCoordinates = null,
  editMode = false,
  editData = null,
  preloadedImage = null,  // Add preloaded image support
  initialContent = '',  // Add initial content support
  postName = ''  // Add post name for scheduling (e.g., festival name like "New Year")
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedGeotag, setSelectedGeotag] = useState(null);
  const [geotagStatus, setGeotagStatus] = useState(null);
  const geotagStatusTimeoutRef = useRef(null);
  const [showBestTimes, setShowBestTimes] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const aiBusy = aiLoading;

  const showGeotagStatus = (severity, message, duration = 5000) => {
    if (!message) return;
    setGeotagStatus({ severity, message });
    if (geotagStatusTimeoutRef.current) {
      clearTimeout(geotagStatusTimeoutRef.current);
    }
    geotagStatusTimeoutRef.current = setTimeout(() => {
      setGeotagStatus(null);
      geotagStatusTimeoutRef.current = null;
    }, duration);
  };

  React.useEffect(() => () => {
    if (geotagStatusTimeoutRef.current) {
      clearTimeout(geotagStatusTimeoutRef.current);
    }
  }, []);

  // Get best posting times based on business category
  const bestTimes = getBestPostingTimes(businessCategory);

  const [formData, setFormData] = useState({
    summary: initialContent || '',  // Use initial content if provided
    mediaUrl: '',
    actionType: 'LEARN_MORE',
    actionUrl: '',
    topicType: 'STANDARD',
    scheduledDate: '',
    scheduledTime: ''
  });

  const resolvedBusinessAddress = useMemo(
    () => formatAddressString(businessAddress),
    [businessAddress]
  );

  const geotagContext = useMemo(() => {
    const addressPayload = typeof businessAddress === 'object'
      ? businessAddress
      : resolvedBusinessAddress
        ? { locality: resolvedBusinessAddress }
        : null;

    const profileStub = {
      latlng: businessCoordinates,
      selectedKeywords: businessKeywords,
      title: businessName,
      name: businessName
    };

    if (businessCategory) {
      profileStub.categories = {
        primaryCategory: { displayName: businessCategory }
      };
    }

    if (addressPayload) {
      profileStub.storefrontAddress = addressPayload;
    }

    return {
      businessProfileData: profileStub,
      businessCoordinates,
      businessKeywords,
      category: businessCategory,
      businessName,
      address: resolvedBusinessAddress
    };
  }, [businessAddress, businessCategory, businessCoordinates, businessKeywords, businessName, resolvedBusinessAddress]);

  const keywordHints = useMemo(
    () =>
      buildKeywordHints({
        sourceKeywords: businessKeywords,
        businessCategory,
        topicType: formData.topicType
      }),
    [businessKeywords, businessCategory, formData.topicType]
  );

  const geotagDefaults = useMemo(
    () => GeotagService.buildDefaultMetadata({
      ...geotagContext,
      existingMetadata: selectedGeotag
    }),
    [geotagContext, selectedGeotag]
  );

  const seedContentHint = useMemo(() => {
    const source = initialContent || '';
    return source.replace(/\s+/g, ' ').trim();
  }, [initialContent]);

  // Handle preloaded image and initial content when modal opens
  React.useEffect(() => {
    let objectUrl = null;

    if (open) {
      // Set preloaded image if provided
      if (preloadedImage) {
        // Check if it's a File object or base64 string
        if (preloadedImage instanceof File) {
          // It's already a File object from html2canvas
          setSelectedFile(preloadedImage);
          setSelectedGeotag(null);
          objectUrl = URL.createObjectURL(preloadedImage);
          setPreviewUrl(objectUrl);
          logger.log('[SchedulePostModal] Preloaded File set:', preloadedImage.name, preloadedImage.size, 'bytes');
        } else if (typeof preloadedImage === 'string') {
          // It's a base64 string - convert to File
          setPreviewUrl(preloadedImage);
          logger.log('[SchedulePostModal] Preloaded base64 image set:', preloadedImage.substring(0, 50) + '...');

          try {
            const base64Response = preloadedImage.split(',')[1];
            const byteCharacters = atob(base64Response);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/png' });
            const file = new File([blob], 'generated-post.png', { type: 'image/png' });
            setSelectedFile(file);
            setSelectedGeotag(null);
            logger.log('[SchedulePostModal] Converted base64 to File:', file.name, file.size, 'bytes');
          } catch (error) {
            logger.error('[SchedulePostModal] Error converting base64 to File:', error);
          }
        }
      }

      // Set initial content if provided
      if (initialContent && !editMode) {
        setFormData(prev => ({
          ...prev,
          summary: initialContent
        }));
        logger.log('[SchedulePostModal] Initial content set:', initialContent.substring(0, 100) + '...');
      }
    }

    // Cleanup function to revoke object URL
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, preloadedImage, initialContent, editMode]);

  // Populate form when editing
  React.useEffect(() => {
    if (editMode && editData && open) {
      const scheduledDateTime = new Date(editData.scheduledTime);
      const dateStr = scheduledDateTime.toISOString().split('T')[0];
      const timeStr = scheduledDateTime.toTimeString().slice(0, 5);

      setFormData({
        summary: editData.summary || '',
        mediaUrl: editData.mediaUrl || '',
        actionType: editData.actionType || 'LEARN_MORE',
        actionUrl: editData.actionUrl || '',
        topicType: editData.topicType || 'STANDARD',
        scheduledDate: dateStr,
        scheduledTime: timeStr
      });

      if (editData.mediaUrl) {
        setPreviewUrl(editData.mediaUrl);
      }
    } else if (!open) {
      // Reset form when modal closes
      setFormData({
        summary: initialContent || '',  // Reset to initial content or empty
        mediaUrl: '',
        actionType: 'LEARN_MORE',
        actionUrl: '',
        topicType: 'STANDARD',
        scheduledDate: '',
        scheduledTime: ''
      });
      setSelectedFile(null);
      setSelectedGeotag(null);
      setPreviewUrl('');
      setError('');
      setSuccess(false);
    }
  }, [editMode, editData, open, initialContent]);

  const actionTypes = [
    { value: 'LEARN_MORE', label: 'Learn More' },
    { value: 'SIGN_UP', label: 'Sign Up' },
    { value: 'CALL', label: 'Call' },
    { value: 'BOOK', label: 'Book' },
    { value: 'ORDER', label: 'Order' },
    { value: 'SHOP', label: 'Shop' }
  ];

  const topicTypes = [
    { value: 'STANDARD', label: 'Standard' },
    { value: 'EVENT', label: 'Event' },
    { value: 'OFFER', label: 'Offer' },
    { value: 'ALERT', label: 'Alert' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid image format. Only JPG, PNG, GIF, and WebP files are supported.');
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Image file size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setSelectedGeotag(null);
    setGeotagStatus(null);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    logger.log('[SchedulePostModal] Image selected, will be attached to post when created');

    // ============ AUTO-APPLY GEOTAG ============
    // Automatically apply geotag if we have business coordinates
    if (geotagDefaults?.latitude && geotagDefaults?.longitude) {
      try {
        showGeotagStatus('info', '📍 Auto-applying geotag from business location...');
        const { applyGeotagMetadata } = await import('../../../utils/geotagging');
        const result = await applyGeotagMetadata(file, geotagDefaults);

        if (result.applied && result.file) {
          setSelectedFile(result.file);
          setSelectedGeotag(geotagDefaults);
          // Update preview with geotagged file
          const geotaggedReader = new FileReader();
          geotaggedReader.onloadend = () => {
            setPreviewUrl(geotaggedReader.result);
          };
          geotaggedReader.readAsDataURL(result.file);
          showGeotagStatus('success', `✅ Geotag auto-applied: ${geotagDefaults.latitude.toFixed(4)}, ${geotagDefaults.longitude.toFixed(4)}`);
          logger.log('[SchedulePostModal] Geotag auto-applied successfully');
        } else {
          showGeotagStatus('warning', '⚠️ Could not auto-apply geotag. Click "Geotag" button to try manually.');
          logger.warn('[SchedulePostModal] Auto-geotag failed:', result.reason);
        }
      } catch (geotagError) {
        showGeotagStatus('warning', '⚠️ Geotag auto-apply failed. You can try manually using the Geotag button.');
        logger.error('[SchedulePostModal] Auto-geotag error:', geotagError);
      }
    } else {
      showGeotagStatus('warning', '⚠️ Business coordinates not found. Please verify your business profile has a valid address.');
      logger.warn('[SchedulePostModal] No business coordinates available for auto-geotag');
    }
    // ============================================
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    handleInputChange('mediaUrl', '');
    setSelectedGeotag(null);
    setGeotagStatus(null);
  };

  const handleAutoGeotag = async () => {
    if (!selectedFile) {
      showGeotagStatus('error', 'Upload or select an image before applying geotag.');
      return;
    }

    if (!geotagDefaults?.latitude || !geotagDefaults?.longitude) {
      showGeotagStatus(
        'error',
        'Unable to determine location data. Please verify your business profile address.'
      );
      return;
    }

    try {
      await handleApplyGeotag(geotagDefaults);
    } catch (applyError) {
      logger.error('[SchedulePostModal] Geotag application encountered an error:', applyError);
      showGeotagStatus('error', 'Failed to apply geotag metadata. Please try again.');
    }
  };

  const logGeotagInspection = async (file, phase) => {
    if (!file) return;
    try {
      const report = await inspectGeotagMetadata(file);
      if (report.success && report.embedded) {
        logger.log(`[SchedulePostModal] ${phase} geotag verification:`, report.embedded);
      } else {
        logger.warn(
          `[SchedulePostModal] ${phase} geotag verification failed:`,
          report.reason || 'No metadata detected'
        );
      }
    } catch (inspectionError) {
      logger.warn(`[SchedulePostModal] ${phase} geotag verification error:`, inspectionError);
    }
  };

  const handleApplyGeotag = async (metadata) => {
    const normalized = normalizeGeotagMetadata(metadata);
    if (selectedFile) {
      const result = await applyGeotagMetadata(selectedFile, normalized);
      if (result.applied && result.file) {
        setSelectedFile(result.file);
        setSelectedGeotag(normalized);
        try {
          const refreshedPreview = await readFilePreview(result.file);
          setPreviewUrl(refreshedPreview);
        } catch (previewError) {
          logger.warn('[SchedulePostModal] Failed to refresh preview after geotagging:', previewError);
        }
        logger.log('[SchedulePostModal] Geotag metadata embedded for scheduled post image');
        showGeotagStatus('success', 'Geotag embedded into the selected photo.');
        await logGeotagInspection(result.file, 'Apply');
      } else if (result.reason) {
        logger.warn('[SchedulePostModal] Geotag embedding skipped:', result.reason);
        showGeotagStatus('warning', `Could not embed geotag metadata: ${result.reason}`);
        setSelectedGeotag(normalized);
      } else if (result.error) {
        logger.error('[SchedulePostModal] Geotag embedding failed:', result.error);
        showGeotagStatus('error', 'Failed to embed geotag metadata. Please try again.');
        setSelectedGeotag(normalized);
      } else {
        showGeotagStatus('error', 'Failed to embed geotag metadata. Please try again.');
        setSelectedGeotag(normalized);
      }
    } else {
      setSelectedGeotag(normalized);
      showGeotagStatus('info', 'Geotag metadata saved. It will be applied after an image is uploaded.');
    }
  };

  const handleDownloadTaggedImage = async () => {
    if (!selectedFile) return;
    try {
      await logGeotagInspection(selectedFile, 'Download');
      const originalName = selectedFile.name || 'image';
      const lastDot = originalName.lastIndexOf('.');
      const baseName = (lastDot > 0 ? originalName.slice(0, lastDot) : originalName) || 'image';
      const extFromName = lastDot > 0 ? originalName.slice(lastDot + 1) : '';
      const extFromType = selectedFile.type?.split('/').pop() || '';
      const safeExtension = (extFromName || extFromType || 'jpg').replace(/[^0-9a-zA-Z]/g, '') || 'jpg';
      const downloadName = `${baseName}-geotagged.${safeExtension}`;
      const blobUrl = URL.createObjectURL(selectedFile);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = downloadName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
      logger.log('[SchedulePostModal] Geotagged image download triggered for verification');
    } catch (downloadError) {
      logger.warn('[SchedulePostModal] Failed to download geotagged image:', downloadError);
      alert('Unable to download the image. Please try again.');
    }
  };

  const generateVisionPost = async () => {
    if (!previewUrl && !selectedFile) return null;

    try {
      let dataUrl = '';
      if (previewUrl && previewUrl.startsWith('data:image')) {
        dataUrl = previewUrl;
      } else if (selectedFile) {
        dataUrl = await convertFileToDataUrl(selectedFile);
      } else if (previewUrl) {
        try {
          dataUrl = await fetchImageAsDataUrl(previewUrl);
        } catch (error) {
          logger.warn('[SchedulePostModal] Failed to resolve preview URL for vision post generation:', error);
        }
      }

      if (!dataUrl) return null;

      const prompt = `You write concise, high-converting Google Business Profile posts. Respond only with compact JSON: {"post": string, "keywords": string[], "hashtags": string[]}. The post must be <= 750 characters, plain text, no emojis, include a clear CTA, integrate provided keywords naturally, and include local context. Use up to 8 relevant hashtags without spaces. Analyze the uploaded image carefully and describe what is actually visible (subjects, people, signage, products, scenery, mood) so the copy matches the photo. Provided keywords (use naturally, no hashtags inside the post): ${keywordHints.length ? keywordHints.join(', ') : 'choose relevant local service keywords'
        }. Return ONLY the JSON object.`;

      const response = await ApiService.post('/customer/ai/describe-image', {
        imageBase64: dataUrl,
        prompt,
        context: {
          business_name: businessName || '',
          category: businessCategory || '',
          city: resolvedBusinessAddress || '',
        }
      });

      const raw = response?.data?.text || response?.text || '';
      if (!raw) return null;

      const parsed = extractJsonPayload(raw);
      if (parsed?.post) {
        return {
          post: String(parsed.post || '').trim(),
          keywords: sanitizeKeywordArray(parsed.keywords),
          hashtags: sanitizeHashtagArray(parsed.hashtags)
        };
      }

      return null;
    } catch (error) {
      logger.warn('[SchedulePostModal] Vision post generation failed:', error);
      return null;
    }
  };

  // Generate AI-powered post summary
  const handleAISuggestSummary = async () => {
    setAiLoading(true);
    setAiError('');

    try {
      logger.log('[SchedulePostModal] Generating AI post summary...');

      let summaryGenerated = false;
      if (selectedFile || previewUrl) {
        const visionResult = await generateVisionPost();
        if (visionResult?.post) {
          const hashtagsLine = sanitizeHashtagArray(visionResult.hashtags).join(' ');
          const postText = String(visionResult.post || '').trim();
          const combined = hashtagsLine ? `${postText}\n\n${hashtagsLine}` : postText;
          setFormData(prev => ({ ...prev, summary: combined }));
          summaryGenerated = true;
          logger.log('[SchedulePostModal] ✅ Vision-based post generated successfully');
        }
      }

      if (summaryGenerated) {
        return;
      }

      const topicDescriptor = TOPIC_DESCRIPTIONS[formData.topicType] || 'business update';
      const selectedActionLabel = actionTypes.find(action => action.value === formData.actionType)?.label || 'Learn More';
      const keywordInstruction = keywordHints.length
        ? `Integrate these keywords naturally (no hashtags): ${keywordHints.join(', ')}.`
        : 'Choose 2-3 SEO-friendly keywords that match the business and weave them naturally (no hashtags).';
      const addressInstruction = resolvedBusinessAddress
        ? `Mention the business address ("${resolvedBusinessAddress}") once so locals know where to visit.`
        : 'Reference the neighbourhood or service area once so locals know where to find you.';
      const actionInstruction = selectedActionLabel
        ? `End with a "${selectedActionLabel}" style call-to-action.`
        : 'End with an inviting call-to-action.';
      const linkInstruction = formData.actionUrl
        ? `If it feels natural, reference this URL (${formData.actionUrl}) without pasting it more than once.`
        : '';
      const imageHints = buildImageHint(!!(selectedFile || previewUrl), topicDescriptor);

      const prompt = `You are a professional social media strategist creating a Google Business Profile caption for the image described below. Task: provide description for this post for Google Business Profile post; hashtags will be generated later.

Business Snapshot:
- Name: ${businessName || 'Our business'}
- Category: ${businessCategory || 'Local service'}
${resolvedBusinessAddress ? `- Address: ${resolvedBusinessAddress}\n` : ''}- Topic: ${topicDescriptor}
- Call-to-action: ${selectedActionLabel}
${formData.actionUrl ? `- Landing URL: ${formData.actionUrl}\n` : ''}${seedContentHint ? `- Post inspiration: ${seedContentHint}\n` : ''} 
Image Context:
${imageHints}

Instructions:
- Craft a ${selectedFile ? 'photo-specific' : 'story-driven'} caption between 70 and 120 words in a professional yet friendly tone.
- Start by referencing what a viewer sees in the image, then connect it to the offer or update.
- ${keywordInstruction}
- ${addressInstruction}
- ${actionInstruction}
${linkInstruction ? `- ${linkInstruction}\n` : ''}- Use at most two relevant emojis.
- Never mention filenames, template codes, or internal identifiers (e.g., "visibeen edm 123").
- Do NOT include hashtags (they will be added later).
- Keep sentences short, active, and focused on customer benefits.
- Mention the business name in the opening sentence or clause.

Return ONLY the caption text (no labels or bullet points).`;

      const { generatePost, generateHashtags } = await import('../../../services/gmbRanking/geminiService');
      const generatedText = await generatePost({
        businessName: businessName || 'Our business',
        businessCategory: businessCategory || 'Local service',
        businessAddress: resolvedBusinessAddress,
        keywords: keywordHints,
        topicType: topicDescriptor,
        callToAction: selectedActionLabel,
        actionUrl: formData.actionUrl,
        imageHints,
        prompt
      });

      if (generatedText) {
        const cleanedText = generatedText.trim()
          .replace(/^["']|["']$/g, '')
          .replace(/\n{3,}/g, '\n\n');

        let finalSummary = cleanedText;
        try {
          const hashtagString = await generateHashtags({
            summary: cleanedText,
            keywords: keywordHints,
            businessName: businessName || 'Our business',
            businessAddress: resolvedBusinessAddress
          });

          const hashtags = sanitizeHashtagOutput(hashtagString).join(' ');
          if (hashtags) {
            finalSummary = `${cleanedText}\n\n${hashtags}`;
          }
        } catch (hashtagError) {
          logger.warn('[SchedulePostModal] Hashtag generation failed, continuing without hashtags:', hashtagError);
        }

        setFormData(prev => ({ ...prev, summary: finalSummary }));
        logger.log('[SchedulePostModal] ✅ AI summary generated successfully');
      } else {
        throw new Error('No content generated');
      }

    } catch (error) {
      logger.error('[SchedulePostModal] AI summary generation failed:', error);
      setAiError(error.message || 'Failed to generate AI summary. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.summary.trim()) {
      setError('Summary is required');
      return false;
    }
    if (!formData.scheduledDate || !formData.scheduledTime) {
      setError('Please select both date and time for scheduling');
      return false;
    }
    if (formData.actionType !== 'CALL' && !formData.actionUrl.trim()) {
      setError('Action URL is required for this action type');
      return false;
    }
    return true;
  };

  const handleSchedulePost = async () => {
    if (!validateForm()) return;

    // If taskCreationMode is true (admin task creation mode), skip GMB posting
    if (taskCreationMode) {
      logger.log('[SchedulePostModal] Admin task creation mode - preparing post data without GMB posting');

      setLoading(true);

      try {
        // Create scheduled time from date and time inputs
        const scheduledDateTime = formData.scheduledDate && formData.scheduledTime
          ? new Date(`${formData.scheduledDate}T${formData.scheduledTime}`)
          : new Date();
        const scheduledISOTime = scheduledDateTime.toISOString();

        // Convert file to base64 if present (for serialization)
        let mediaBase64 = null;
        if (selectedFile) {
          try {
            mediaBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(selectedFile);
            });
            logger.log('[SchedulePostModal] Converted image to base64 for task storage');
          } catch (err) {
            logger.error('[SchedulePostModal] Failed to convert image to base64:', err);
          }
        }

        // Prepare post data for task creation
        const postDataForTask = {
          summary: formData.summary,
          topicType: formData.topicType,
          callToAction: formData.actionType ? {
            actionType: formData.actionType,
            url: formData.actionUrl || ''
          } : null,
          media: mediaBase64 ? [{
            mediaFormat: 'PHOTO',
            sourceUrl: mediaBase64  // Store as base64 data URL
          }] : [],
          scheduledFor: scheduledISOTime,
          scheduledTime: scheduledISOTime
        };

        if (selectedGeotag) {
          const metadata = normalizeGeotagMetadata(selectedGeotag);
          if (metadata.latitude != null && metadata.longitude != null) {
            postDataForTask.geotag = metadata;
          }
        }

        logger.log('[SchedulePostModal] Calling onSuccess with post data (media as base64):', {
          ...postDataForTask,
          media: postDataForTask.media.length > 0 ? [{
            mediaFormat: postDataForTask.media[0].mediaFormat,
            sourceUrl: postDataForTask.media[0].sourceUrl.substring(0, 50) + '...'
          }] : []
        });

        // Call onSuccess callback (for admin task creation)
        await onSuccess(postDataForTask);

        setSuccess(true);
        setLoading(false);

        // Don't close modal immediately - let the parent handle it
        return;

      } catch (err) {
        logger.error('[SchedulePostModal] Error in task creation mode:', err);
        setError(err.message || 'Failed to create task with post');
        setLoading(false);
        return;
      }
    }

    // Regular GMB posting mode (for direct posts)
    // 1. Get robust access token (check multiple common keys)
    const googleAccessToken = sessionStorage.getItem('google_access_token') ||
      localStorage.getItem('google_access_token') ||
      sessionStorage.getItem('googleAccessToken') ||
      localStorage.getItem('googleAccessToken');

    // 2. Clean IDs by removing prefixes
    const cleanAccountId = accountId?.replace('accounts/', '');
    const cleanLocationId = locationId?.replace('locations/', '');

    // Validate GMB account and location
    if (!cleanAccountId || !cleanLocationId) {
      setError('Please select a Google My Business account and location first. Go to Business Profile page to connect your account.');
      logger.error('[SchedulePostModal] Missing GMB configuration:', {
        accountId: cleanAccountId,
        locationId: cleanLocationId
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      logger.log('[SchedulePostModal] Creating GMB Local Post in feed...');
      logger.log('[SchedulePostModal] GMB Configuration:', {
        accountId: cleanAccountId,
        locationId: cleanLocationId
      });

      if (!googleAccessToken) {
        throw new Error('Google access token not found. Please sign in with Google.');
      }

      // Create FormData for the post
      const postFormData = new FormData();

      // Log the values being appended
      logger.log('[SchedulePostModal] Appending GMB IDs to FormData:', {
        accountId: cleanAccountId,
        locationId: cleanLocationId
      });

      postFormData.append('accountId', cleanAccountId);
      postFormData.append('locationId', cleanLocationId);
      postFormData.append('summary', formData.summary);
      postFormData.append('topicType', formData.topicType);

      // Add photo if selected
      if (selectedFile) {
        postFormData.append('photo', selectedFile);
        if (selectedGeotag) {
          const metadata = normalizeGeotagMetadata(selectedGeotag);
          if (metadata.latitude != null && metadata.longitude != null) {
            postFormData.append('geotag', JSON.stringify(metadata));
          }
        }
        logger.log('[SchedulePostModal] Photo will be attached to post');
      }

      // Add call to action if provided
      if (formData.actionType) {
        postFormData.append('actionType', formData.actionType);
        if (formData.actionUrl && formData.actionType !== 'CALL') {
          postFormData.append('actionUrl', formData.actionUrl);
        }
      }

      // If schedule is in the future, use our server-side scheduler instead of posting now
      let result;
      if (formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        const now = new Date();
        if (scheduledDateTime > now) {
          logger.log('[SchedulePostModal] Scheduling post via server-side scheduler for:', scheduledDateTime.toLocaleString());

          const scheduleFormData = new FormData();
          scheduleFormData.append('accountId', cleanAccountId);
          scheduleFormData.append('locationId', cleanLocationId);
          scheduleFormData.append('post_name', postName || `GMB Scheduled Post${businessName ? ' - ' + businessName : ''}`);
          scheduleFormData.append('title', formData.summary.substring(0, 50));
          scheduleFormData.append('scheduled_time', scheduledDateTime.toISOString());
          scheduleFormData.append('summary', formData.summary);
          scheduleFormData.append('topicType', formData.topicType);
          if (formData.actionType) {
            scheduleFormData.append('actionType', formData.actionType);
            if (formData.actionUrl && formData.actionType !== 'CALL') {
              scheduleFormData.append('actionUrl', formData.actionUrl);
            }
          }
          if (selectedFile) {
            scheduleFormData.append('image', selectedFile);
            if (selectedGeotag) {
              const metadata = normalizeGeotagMetadata(selectedGeotag);
              if (metadata.latitude != null && metadata.longitude != null) {
                scheduleFormData.append('geotag', JSON.stringify(metadata));
              }
            }
          }

          // Use app auth (ApiService adds it); do NOT send Google token here
          result = await ApiService.request('/post-scheduler/schedule-with-image', {
            method: 'POST',
            body: scheduleFormData
          });
        }
      }

      // If not scheduled (no future time), post immediately to GMB
      if (!result) {
        logger.log('[SchedulePostModal] No future schedule detected, posting immediately');
        result = await ApiService.request('/gmb/create-local-post', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${googleAccessToken}`
          },
          body: postFormData
        });
      }

      logger.log('🔍 [SchedulePostModal] API Response:', result);
      logger.log('🔍 [SchedulePostModal] API Response structure:', {
        hasResult: !!result,
        hasSuccess: result?.success,
        hasData: !!result?.data,
        hasLocalPost: !!result?.data?.localPost,
        message: result?.message
      });

      // Check for success: REST.success shape is { error, code, data, message }
      const isSuccess = !!result && (
        result.code === 200 ||
        !!result.data?.localPost ||
        !!result.data?.post
      );
      if (!isSuccess) {
        throw new Error(result?.message || 'Failed to create post');
      }

      // In schedule mode, backend returns { post, mediaUrl } ; in immediate mode, returns { localPost }
      if (result?.data?.localPost) {
        logger.log('[SchedulePostModal] Post created successfully in GMB feed:', result.data.localPost);
      } else if (result?.data?.post) {
        logger.log('[SchedulePostModal] Post scheduled successfully with ID:', result.data.post.id);
      }
      setSuccess(true);

      // Create scheduled time from date and time inputs
      const scheduledDateTime = (formData.scheduledDate && formData.scheduledTime)
        ? new Date(`${formData.scheduledDate}T${formData.scheduledTime}`)
        : null;
      const scheduledISOTime = scheduledDateTime ? scheduledDateTime.toISOString() : null;

      logger.log('🎯 [SchedulePostModal] Scheduled date/time:', {
        date: formData.scheduledDate,
        time: formData.scheduledTime,
        combined: scheduledISOTime
      });

      // Notify parent component with scheduled post data
      const postDataForCallback = result?.data?.localPost ? {
        summary: result.data.localPost.summary,
        topicType: result.data.localPost.topicType,
        mediaUrl: result.data.localPost.media?.[0]?.sourceUrl || '',
        actionType: result.data.localPost.callToAction?.actionType || '',
        actionUrl: result.data.localPost.callToAction?.url || '',
        scheduledFor: scheduledISOTime,
        scheduledTime: scheduledISOTime,
        createdAt: result.data.localPost.createTime,
        published: true,
        postName: result.data.localPost.name
      } : result?.data?.post ? {
        summary: formData.summary,
        topicType: formData.topicType,
        mediaUrl: result.data.mediaUrl || '',
        actionType: formData.actionType || '',
        actionUrl: formData.actionUrl || '',
        scheduledFor: scheduledISOTime,
        scheduledTime: scheduledISOTime,
        createdAt: new Date().toISOString(),
        published: false,
        postId: result.data.post.id
      } : null;

      if (postDataForCallback) {
        // Call both callbacks for compatibility
        if (onPostScheduled) {
          logger.log('📤 [SchedulePostModal] Calling onPostScheduled:', postDataForCallback);
          onPostScheduled(postDataForCallback, editMode);
        }
        if (onSuccess) {
          logger.log('📤 [SchedulePostModal] Calling onSuccess:', postDataForCallback);
          await onSuccess(postDataForCallback);
        }
      } else {
        logger.warn('⚠️ [SchedulePostModal] No recognizable post data in response');
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);

    } catch (err) {
      logger.error('[SchedulePostModal] Error creating post:', err);

      // Check if error is due to invalid GMB location/account
      const errorMessage = err.message || 'Failed to create post. Please try again.';
      if (errorMessage.includes('GMB location not found') || errorMessage.includes('doesn\'t exist or has been deleted')) {
        setError(
          'Your saved GMB profile is outdated or invalid. Please go to the Dashboard and click "Set as Default" on your current business profile to refresh the connection.'
        );
      } else if (errorMessage.includes('Access denied')) {
        setError(
          'You don\'t have permission to post to this GMB location. Please ensure you have admin access or select a different business profile on the Dashboard.'
        );
      } else if (errorMessage.includes('expired') || errorMessage.includes('Authentication failed')) {
        setError(
          'Your Google session has expired. Please sign in with Google again to reconnect your account.'
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      summary: '',
      mediaUrl: '',
      actionType: 'LEARN_MORE',
      actionUrl: '',
      topicType: 'STANDARD',
      scheduledDate: '',
      scheduledTime: ''
    });
    setSelectedFile(null);
    setSelectedGeotag(null);
    setPreviewUrl('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        fullScreen={false}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            [theme => theme.breakpoints.down('sm')]: {
              margin: 0,
              maxHeight: '100vh',
              height: '100vh',
              borderRadius: 0
            }
          }
        }}
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <CalendarTodayIcon sx={{ color: '#0B91D6' }} />
            {editMode ? 'Edit Scheduled Post' : 'Schedule Post'}
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent dividers>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Post scheduled successfully! 🎉
              </Typography>
              <Typography variant="body2">
                Your post will be automatically published at the scheduled date and time. Keep the page open or return later to ensure publishing.
              </Typography>
              {formData.mediaUrl && formData.mediaUrl.startsWith('data:') && (
                <Typography variant="body2" sx={{ mt: 1, color: '#F59E0B', fontWeight: 500 }}>
                  ⚠️ Note: Uploaded images are stored locally. For better reliability, consider using a publicly accessible image URL.
                </Typography>
              )}
            </Alert>
          )}

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                (error.includes('outdated or invalid') || error.includes('Dashboard')) && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      window.location.href = '/dashboard';
                    }}
                    sx={{ fontWeight: 600 }}
                  >
                    Go to Dashboard
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          )}

          {/* Best Posting Times Recommendation */}
          {showBestTimes && bestTimes && (
            <Box sx={{
              backgroundColor: '#F0F9FF',
              border: '2px solid #0B91D6',
              borderRadius: '12px',
              padding: { xs: '12px', sm: '16px' },
              mb: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography sx={{
                  fontWeight: 600,
                  color: '#0B91D6',
                  fontSize: { xs: '14px', sm: '15px' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  💡 Best Time to Post
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setShowBestTimes(false)}
                  sx={{ mt: -1, mr: -1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: '2px', sm: '8px' },
                  alignItems: { xs: 'flex-start', sm: 'center' }
                }}>
                  <Typography sx={{
                    fontSize: { xs: '12px', sm: '13px' },
                    fontWeight: 600,
                    color: '#374151',
                    minWidth: { sm: '90px' }
                  }}>
                    Best Days:
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: '#1F2937' }}>
                    {bestTimes.days}
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: '2px', sm: '8px' },
                  alignItems: { xs: 'flex-start', sm: 'center' }
                }}>
                  <Typography sx={{
                    fontSize: { xs: '12px', sm: '13px' },
                    fontWeight: 600,
                    color: '#374151',
                    minWidth: { sm: '90px' }
                  }}>
                    Best Times:
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: '#1F2937' }}>
                    {bestTimes.times}
                  </Typography>
                </Box>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: '2px', sm: '8px' },
                  alignItems: { xs: 'flex-start', sm: 'center' }
                }}>
                  <Typography sx={{
                    fontSize: { xs: '12px', sm: '13px' },
                    fontWeight: 600,
                    color: '#374151',
                    minWidth: { sm: '90px' }
                  }}>
                    Frequency:
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: '#1F2937' }}>
                    {bestTimes.frequency}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #BFDBFE' }}>
                  <Typography sx={{
                    fontSize: { xs: '11px', sm: '12px' },
                    color: '#6B7280',
                    fontStyle: 'italic'
                  }}>
                    💬 {bestTimes.reason}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ width: '100%', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                Post Summary
              </Typography>
              <Tooltip title="Generate AI-powered post summary based on your business and topic">
                <Button
                  onClick={handleAISuggestSummary}
                  disabled={loading || success || aiBusy}
                  startIcon={aiBusy ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#7C3AED',
                    background: 'linear-gradient(135deg, #F3E8FF 0%, #EDE9FE 100%)',
                    border: '1px solid #C4B5FD',
                    padding: '6px 14px',
                    borderRadius: '8px',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                      borderColor: '#A78BFA'
                    },
                    '&:disabled': {
                      background: '#F3F4F6',
                      color: '#9CA3AF',
                      border: '1px solid #E5E7EB'
                    }
                  }}
                >
                  {aiBusy ? 'Generating...' : 'AI Suggest'}
                </Button>
              </Tooltip>
            </Box>

            {aiError && (
              <Alert severity="error" sx={{ mb: 1, fontSize: '13px' }} onClose={() => setAiError('')}>
                {aiError}
              </Alert>
            )}

            <TextField
              multiline
              rows={4}
              value={formData.summary}
              onChange={(e) => handleInputChange('summary', e.target.value)}
              placeholder="What would you like to share with your customers? Click 'AI Suggest' for inspiration!"
              fullWidth
              disabled={loading || success}
              inputProps={{ maxLength: 1500 }}
              helperText={`${formData.summary.length}/1500 characters`}
            />
          </Box>

          <Box sx={{
            mb: { xs: 2, sm: 0 },
            width: '100%',
            flexShrink: 0
          }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: '#666666',
                fontWeight: 600,
                fontSize: { xs: '15px', sm: '14px' }
              }}
            >
              Add Image (optional)
            </Typography>
            <Typography
              variant="caption"
              sx={{
                mb: 2,
                color: '#10B981',
                display: 'block',
                fontSize: { xs: '12px', sm: '12px' },
                fontWeight: 500
              }}
            >
              ✓ Image will be attached to your post (not uploaded separately)
            </Typography>

            {previewUrl ? (
              <Box sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 2,
                width: { xs: '100%', sm: 'auto' },
                maxWidth: '100%'
              }}>
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '100%', sm: '200px' },
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '2px solid #E0E0E0',
                    objectFit: 'contain'
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  disabled={loading || success}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: '#EF4444',
                    color: '#FFFFFF',
                    width: 30,
                    height: 30,
                    '&:hover': {
                      backgroundColor: '#DC2626'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>

                {/* Geotag Status Badge on Image */}
                {selectedGeotag?.latitude != null && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    backgroundColor: 'rgba(76, 175, 80, 0.9)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '4px 8px',
                    fontSize: '10px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    📍 Geotagged
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={loading || success}
                  startIcon={<ImageIcon />}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    borderColor: '#0B91D6',
                    color: '#0B91D6',
                    padding: { xs: '14px 20px', sm: '10px 20px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    fontWeight: 600,
                    mb: 2,
                    minHeight: { xs: '52px', sm: 'auto' },
                    '&:hover': {
                      borderColor: '#0A7AB8',
                      backgroundColor: 'rgba(11, 145, 214, 0.08)'
                    }
                  }}
                >
                  Select Image
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                  />
                </Button>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  mb: 2,
                  mt: 1
                }}>
                  <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E0E0E0' }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '13px', sm: '13px' },
                      fontWeight: 600,
                      color: '#999',
                      px: 1
                    }}
                  >
                    OR
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E0E0E0' }} />
                </Box>
              </>
            )}

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<PlaceIcon />}
                onClick={handleAutoGeotag}
                disabled={!selectedFile || loading || success}
              >
                Geotag
              </Button>
              {selectedFile && selectedGeotag && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadTaggedImage}
                  disabled={loading || success}
                >
                  Download tagged image
                </Button>
              )}
              {selectedGeotag?.latitude != null && selectedGeotag?.longitude != null && (
                <Chip
                  label={`Lat ${selectedGeotag.latitude.toFixed(4)}, Lng ${selectedGeotag.longitude.toFixed(4)}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>

            {geotagStatus && (
              <Alert
                severity={geotagStatus.severity}
                icon={false}
                sx={{
                  width: '100%',
                  mt: 1,
                  fontSize: { xs: '12px', sm: '13px' },
                  borderRadius: '8px'
                }}
              >
                {geotagStatus.message}
              </Alert>
            )}

            {!previewUrl && (
              <TextField
                label="Enter image URL"
                value={formData.mediaUrl}
                onChange={(e) => handleInputChange('mediaUrl', e.target.value)}
                placeholder="https://example.com/image.jpg"
                fullWidth
                disabled={loading || success}
                size="medium"
                helperText="JPG, PNG, GIF, or WebP (Max 10MB). For best results, use a publicly accessible URL."
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '15px', sm: '14px' },
                    padding: { xs: '12px', sm: '10px 14px' },
                    minHeight: { xs: '52px', sm: 'auto' }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '15px', sm: '14px' }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '12px', sm: '12px' },
                    marginTop: '8px',
                    lineHeight: 1.4
                  }
                }}
              />
            )}

            {previewUrl && (
              <Alert
                severity="info"
                icon={false}
                sx={{
                  mt: 2,
                  fontSize: { xs: '12px', sm: '12px' },
                  py: 1,
                  borderRadius: '8px'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: { xs: '12px', sm: '12px' } }}
                >
                  📌 Image stored locally. It will be included when the post is published.
                </Typography>
              </Alert>
            )}
          </Box>

          <FormControl
            fullWidth
            disabled={loading || success}
            sx={{ flexShrink: 0 }}
          >
            <InputLabel>Topic Type</InputLabel>
            <Select
              value={formData.topicType}
              onChange={(e) => handleInputChange('topicType', e.target.value)}
              label="Topic Type"
            >
              {topicTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            fullWidth
            disabled={loading || success}
            sx={{ flexShrink: 0 }}
          >
            <InputLabel>Call to Action</InputLabel>
            <Select
              value={formData.actionType}
              onChange={(e) => handleInputChange('actionType', e.target.value)}
              label="Call to Action"
            >
              {actionTypes.map(action => (
                <MenuItem key={action.value} value={action.value}>
                  {action.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.actionType !== 'CALL' && (
            <TextField
              label="Action URL"
              value={formData.actionUrl}
              onChange={(e) => handleInputChange('actionUrl', e.target.value)}
              placeholder="https://your-website.com"
              fullWidth
              disabled={loading || success}
              helperText="URL where users will be directed"
              sx={{ flexShrink: 0 }}
            />
          )}

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '16px',
            width: '100%',
            flexShrink: 0
          }}>
            <TextField
              label="Scheduled Date"
              type="date"
              value={formData.scheduledDate}
              onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
              fullWidth
              disabled={loading || success}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getMinDate() }}
              sx={{
                flex: 1,
                flexShrink: 0,
                minHeight: { xs: '56px', sm: 'auto' }
              }}
            />

            <TextField
              label="Scheduled Time"
              type="time"
              value={formData.scheduledTime}
              onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
              fullWidth
              disabled={loading || success}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: 1,
                flexShrink: 0,
                minHeight: { xs: '56px', sm: 'auto' }
              }}
            />
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: -1,
              flexShrink: 0,
              fontSize: { xs: '12px', sm: '13px' }
            }}
          >
            Your post will be published at the scheduled time
          </Typography>
        </StyledDialogContent>

        <StyledDialogActions>
          <ActionButton
            onClick={handleClose}
            disabled={loading}
            className="secondary"
          >
            Cancel
          </ActionButton>
          <ActionButton
            onClick={handleSchedulePost}
            disabled={loading || success}
            className="primary"
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#FFFFFF' }} />
                {editMode ? 'Updating Post...' : 'Creating Post...'}
              </>
            ) : (
              editMode ? 'Update Post' : 'Schedule Post'
            )}
          </ActionButton>
        </StyledDialogActions>
      </StyledDialog>

    </>
  );
};

export default SchedulePostModal;
